import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useDarkModeCheck } from "../DashboardLayout";
import { GET_USER_INFO } from "../../../service/graphql/query";
import { logout } from "../../../service/graphql/utils";
import {
  SortOrder,
  weightronApiListHeader,
} from "../../../service/utils/constants";
import { ApiConnectionDataEntry } from "../../../service/utils/interfaces";
import { SortSettings } from "../../../component/sortable-table/SortableTable";
import {
  sortTableFunction,
  validateUUID,
} from "../../../service/utils/functions";
import {
  DELETE_API_CONNECTION,
  GET_API_CONNECTIONS,
  TEST_CONNECTION,
  UPDATE_API_CONNECTION,
} from "../../../service/graphql/connections";
import ApiDashboardPageUI from "../../../ui/dashboard/api-dashboard/ApiDashboardUI";
import { parseApiConnections } from "../../../service/utils/parsers";

const ApiDashboardPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  const [headerLine, setHeaderLine] = useState<string[]>([]);
  const [unlistedContent, setUnlistedContent] = useState<
    ApiConnectionDataEntry[]
  >([]);
  const [content, setContent] = useState<any[][]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState("");
  const [reordering, setReordering] = useState(false);

  const [width, setWidth] = useState(0);

  // Edit connection state
  const [openEditConnection, setOpenEditConnection] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState(-1);
  const [isTestingConnection, setIsTestingConnection] = useState(-1);
  const [clientName, setClientName] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [weightronUid, setWeightronUid] = useState("");
  const [weightronSecret, setWeightronSecret] = useState("");
  const [weightronApiKey, setWeightronApiKey] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");

  const [invalidWeightronUid, setInvalidWeightronUid] = useState<string>();
  const [invalidWeightronApiKey, setInvalidWeightronApiKey] =
    useState<string>();

  const [connectionInProgress, setConnectionInProgress] = useState(false);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openTestConnectionDialog, setOpenedTestConnectionDialog] =
    useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });
  const [getApiConnections, { data: apiConnectionsData }] =
    useLazyQuery(GET_API_CONNECTIONS);
  const [saveApiConnection] = useMutation(UPDATE_API_CONNECTION, {
    onError: (err: any) => {
      setSubmissionInProgress(false);
      setIsSuccess(false);
      setSubmitMsg(err.message);
    },
    errorPolicy: "all",
  });
  const [deleteApiConnection] = useMutation(DELETE_API_CONNECTION, {
    errorPolicy: "all",
  });
  const [testConnection] = useMutation(TEST_CONNECTION, {
    onError: (err: any) => {
      setConnectionInProgress(false);
      setIsSuccess(false);
      setSubmitMsg(err.message);
    },
    errorPolicy: "all",
  });

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  useEffect(() => {
    const userData = getUserInfo({ context: { apiName: "middleware" } });
    Promise.all([userData]).then((res) => {
      const user = res[0].data?.getCustomers[0];
      if (user) {
        if (user.category === "Tipping" || !user.pageAuth.weightingPage) {
          window.history.replaceState(null, "", "/");
          navigate("/dashboard/home");
        }
      } else logout(navigate);
    });
  }, [getUserInfo, navigate]);

  useEffect(() => {
    setHeaderLine(weightronApiListHeader);

    getApiConnections({ context: { apiName: "cms" } });

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getApiConnections]);

  useEffect(() => {
    if (
      apiConnectionsData &&
      apiConnectionsData.apiConnections &&
      apiConnectionsData.apiConnections.data
    ) {
      manageApiConnectionsData(apiConnectionsData);
    }
  }, [apiConnectionsData]);

  const manageApiConnectionsData = async (apiConnectionsData: any) => {
    const parsedData = parseApiConnections(
      apiConnectionsData.apiConnections.data
    );
    setUnlistedContent(parsedData.apiList);
    setContent(parsedData.apiTable);
  };

  const changeSortAction = useCallback(
    (col: number) => {
      setSortSettings({
        column: col,
        order:
          sortSettings?.column === col &&
          sortSettings?.order === SortOrder.Ascending
            ? SortOrder.Descending
            : SortOrder.Ascending,
      });
      setReordering(true);
    },
    [sortSettings]
  );

  const changeSearchParam = useCallback((val: string) => {
    setSearchParam(val);
  }, []);

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = content.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setContent(reorderedTable);
    }
  }, [sortSettings, reordering, content]);

  const resetInvalidMsg = useCallback(() => {
    setInvalidWeightronUid(undefined);
    setInvalidWeightronApiKey(undefined);
    setSubmitMsg("");
    setIsSuccess(false);
  }, []);

  const validateCheck = useCallback(
    (content?: ApiConnectionDataEntry) => {
      let isValid = true;
      let apiConnection;
      if (content) apiConnection = content;
      const checkUid = apiConnection
        ? apiConnection.weightron_uid
        : weightronUid;
      const checkApiKey = apiConnection
        ? apiConnection.weightron_key
        : weightronApiKey;
      if (!checkUid || checkUid.length <= 0) {
        setInvalidWeightronUid("Weightron UID is required.");
        isValid = false;
      }
      if (checkUid && !validateUUID(checkUid)) {
        setInvalidWeightronUid("Weightron UID is not valid.");
        isValid = false;
      }
      if (!checkApiKey || checkApiKey.length <= 0) {
        setInvalidWeightronApiKey("Weightron API Key is required.");
        isValid = false;
      }
      return isValid;
    },
    [weightronUid, weightronApiKey]
  );

  const changeSelectedConnectionId = useCallback(
    (id: any) => {
      const apiConnection = unlistedContent.find((item) => item.id === id);
      if (apiConnection) {
        setSelectedConnectionId(id);
        setClientName(apiConnection.name);
        setApplicationId(apiConnection.application_id || "");
        setWeightronUid(apiConnection.weightron_uid || "");
        setWeightronSecret(apiConnection.weightron_secret || "");
        setWeightronApiKey(apiConnection.weightron_key || "");
        setEndpointUrl(apiConnection.endpoint_url || "");
        setIsSuccess(false);
        setSubmitMsg("");
      }
      return apiConnection;
    },
    [unlistedContent]
  );

  const onEvaluateConnection = useCallback(
    async (id?: any) => {
      resetInvalidMsg();
      setConnectionInProgress(true);

      let selectedRowContent;
      if (id) {
        setOpenEditConnection(false);
        setIsTestingConnection(id);
        selectedRowContent = changeSelectedConnectionId(id);
      }

      // Validation section
      const isValid = validateCheck(selectedRowContent);
      if (!isValid) {
        setConnectionInProgress(false);
        return;
      }

      const testInput = {
        name: selectedRowContent?.name || clientName,
        application_id: selectedRowContent?.application_id || applicationId,
        weightron_uid: selectedRowContent?.weightron_uid || weightronUid,
        weightron_secret:
          selectedRowContent?.weightron_secret || weightronSecret,
        weightron_key: selectedRowContent?.weightron_key || weightronApiKey,
      };
      const res = await testConnection({
        variables: { data: testInput },
        context: { apiName: "cms" },
      });
      if (res && res.data && res.data.testConnection) {
        const { status, message } = res.data.testConnection;
        setIsSuccess(status === "OK");
        setSubmitMsg(
          (selectedRowContent ? `${selectedRowContent.name}: ` : "") + message
        );
        setConnectionInProgress(false);
        if (id) setOpenedTestConnectionDialog(true);
      }
    },
    [
      resetInvalidMsg,
      changeSelectedConnectionId,
      validateCheck,
      applicationId,
      clientName,
      weightronUid,
      weightronSecret,
      weightronApiKey,
      testConnection,
    ]
  );

  const onSave = useCallback(async () => {
    resetInvalidMsg();
    setSubmissionInProgress(true);

    // Validation section
    const isValid = validateCheck();
    if (!isValid) {
      setSubmissionInProgress(false);
      return;
    }

    const editInput = {
      id: selectedConnectionId,
      input: {
        weightron_uid: weightronUid,
        weightron_key: weightronApiKey,
        endpoint_url: endpointUrl || "",
      },
    };
    const res = await saveApiConnection({
      variables: editInput,
      context: { apiName: "cms" },
    });
    if (res && res.data && res.data.updateApiConnection) {
      setIsSuccess(true);
      setSubmitMsg("Connection updated successfully");
      setSubmissionInProgress(false);
      setOpenEditConnection(false);
      getApiConnections({ context: { apiName: "cms" } });
    }
  }, [
    resetInvalidMsg,
    validateCheck,
    endpointUrl,
    saveApiConnection,
    selectedConnectionId,
    weightronUid,
    weightronApiKey,
    getApiConnections,
  ]);

  const openEditConnectionTrigger = useCallback(
    (id: any) => {
      changeSelectedConnectionId(id);
      setOpenEditConnection(true);
    },
    [changeSelectedConnectionId]
  );

  const onConfirmDeleteConnection = useCallback((id: any) => {
    setSelectedConnectionId(id);
    setOpenDeleteDialog(true);
  }, []);

  const onDeleteConnection = useCallback(
    async (id: any) => {
      setOpenDeleteDialog(false);
      setOpenEditConnection(false);
      await deleteApiConnection({
        variables: { id },
        context: { apiName: "cms" },
      });
      getApiConnections({ context: { apiName: "cms" } });
    },
    [deleteApiConnection, getApiConnections]
  );

  const openTestConnectionDialogTrigger = useCallback((value: boolean) => {
    if (!value) setIsTestingConnection(-1);
    setOpenedTestConnectionDialog(value);
  }, []);

  const apiDashboardPageProps = useMemo(
    () => ({
      headerLine,
      content,
      sortSettings,
      searchParam,
      changeSortAction,
      changeSearchParam,
      selectedConnectionId,
      clientName,
      applicationId,
      weightronUid,
      weightronSecret,
      weightronApiKey,
      endpointUrl,
      connectionInProgress,
      submissionInProgress,
      invalidWeightronUid,
      invalidWeightronApiKey,
      isTestingConnection,
      isSuccess,
      submitMsg,
      setClientName,
      setApplicationId,
      setWeightronUid,
      setWeightronSecret,
      setWeightronApiKey,
      setEndpointUrl,
      onEvaluateConnection,
      onSave,
      resetInvalidMsg,
      onOpenEditConnection: openEditConnectionTrigger,
      onConfirmDeleteConnection,
      onDeleteConnection,
      setOpenDeleteDialog,
      setOpenedTestConnectionDialog: openTestConnectionDialogTrigger,
      width,
      darkMode,
      openEditConnection,
      openDeleteDialog,
      openTestConnectionDialog,
    }),
    [
      headerLine,
      content,
      sortSettings,
      searchParam,
      changeSortAction,
      changeSearchParam,
      selectedConnectionId,
      clientName,
      applicationId,
      weightronUid,
      weightronSecret,
      weightronApiKey,
      endpointUrl,
      connectionInProgress,
      submissionInProgress,
      invalidWeightronUid,
      invalidWeightronApiKey,
      isTestingConnection,
      isSuccess,
      submitMsg,
      setClientName,
      setApplicationId,
      setWeightronUid,
      setWeightronSecret,
      setWeightronApiKey,
      setEndpointUrl,
      onEvaluateConnection,
      onSave,
      resetInvalidMsg,
      openEditConnectionTrigger,
      onConfirmDeleteConnection,
      onDeleteConnection,
      setOpenDeleteDialog,
      openTestConnectionDialogTrigger,
      width,
      darkMode,
      openEditConnection,
      openDeleteDialog,
      openTestConnectionDialog,
    ]
  );

  return <ApiDashboardPageUI {...apiDashboardPageProps} />;
};

export default ApiDashboardPage;
